import { allLanguages } from '@tanstack/highlight';
import {
	createHighlighter,
	type HighlightDecoration,
	type HighlightRenderNode,
	renderTokens,
} from '@tanstack/highlight/core';

// INFO: module-scope singleton. createHighlighter has no async init, so the server
// and the browser share one registry with no loading step and no hydration drift.
const highlighter = createHighlighter({
	fallbackLanguage: 'plaintext',
	languages: allLanguages,
});

const DIFF_PREFIX = /^([+-]) /;

type HighlightCodeParams = {
	code: string;
	/** Fence metadata line list, e.g. `2,4-6`. */
	highlight?: string;
	lang?: string;
	lineNumbers?: boolean;
};

export type HighlightedCode = {
	code: string;
	lang: string;
	nodes: HighlightRenderNode[];
};

export type { HighlightRenderNode };

/** Removes `+ `/`- ` line prefixes so copied text matches what the block shows. */
export function stripDiffPrefixes(code: string) {
	return code
		.split('\n')
		.map((line) => (DIFF_PREFIX.test(line) ? line.slice(2) : line))
		.join('\n');
}

/** Strips `+ `/`- ` line prefixes and records a diff decoration per prefixed line. */
function extractDiff(code: string) {
	const decorations: HighlightDecoration[] = [];
	const lines = code.split('\n').map((line, index) => {
		const match = DIFF_PREFIX.exec(line);
		if (!match) return line;
		const kind = match[1] === '+' ? 'added' : 'removed';
		decorations.push({
			lines: index + 1,
			className: `diff-${kind}`,
		});
		return line.slice(2);
	});

	return { code: lines.join('\n'), decorations };
}

/** Parses `2,4-6` fence metadata into inclusive highlighted-line ranges. */
function parseHighlightedLines(highlight?: string): HighlightDecoration[] {
	if (!highlight) return [];

	const decorations: HighlightDecoration[] = [];
	for (const part of highlight.split(',')) {
		const bounds = part.trim().split('-').map(Number);
		if (bounds.some(Number.isNaN) || bounds[0] < 1) continue;
		const [start, end = start] = bounds;
		decorations.push({
			lines: start === end ? start : [start, end],
			className: 'normal',
		});
	}

	return decorations;
}

export function highlightCode({
	code,
	highlight,
	lang,
	lineNumbers,
}: HighlightCodeParams): HighlightedCode {
	const diff = extractDiff(code);
	const decorations = [
		...diff.decorations,
		...parseHighlightedLines(highlight),
	];
	const resolvedLang = highlighter.normalizeLanguage(lang);
	const { tokens } = highlighter.tokenize(diff.code, { lang: resolvedLang });

	return {
		code: diff.code,
		lang: resolvedLang,
		nodes: renderTokens(tokens, { decorations, lineNumbers }),
	};
}
